import type { ComponentProps, ComponentPropsWithoutRef } from "react";
import { createContext, useContext } from "react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { checkableStyles } from "./styles";

const radioGroupVariants = tv({
  extend: checkableStyles,
  slots: {
    group: "grid gap-2",
    input: "flex items-center justify-center rounded-full",
    indicator: "rounded-full bg-secondary-500 in-disabled:bg-neutral-600",
  },
  variants: {
    isError: {
      true: {
        indicator: "bg-red-300",
      },
    },
    size: {
      base: { input: "translate-y-1", indicator: "size-2" },
      lg: { input: "translate-y-0.5", indicator: "size-2.5" },
    },
  },
});

const { group, itemRoot, input, indicator, label, description } =
  radioGroupVariants();

type RadioGroupVariants = VariantProps<typeof radioGroupVariants>;

const RadioGroupContext = createContext<RadioGroupVariants>({});

const useRadioGroupContext = () => {
  return useContext(RadioGroupContext);
};

type RadioGroupRootProps = ComponentProps<typeof RadioGroupPrimitive.Root> &
  Omit<RadioGroupVariants, "isError">;

const Root = ({
  size,
  isCard,
  hideIndicator,
  className,
  "aria-invalid": ariaInvalid,
  ...props
}: RadioGroupRootProps) => {
  const isError = typeof ariaInvalid === "boolean" ? ariaInvalid : undefined;

  return (
    <RadioGroupContext.Provider
      value={{ size, isCard, isError, hideIndicator }}
    >
      <RadioGroupPrimitive.Root
        data-slot="radio-group"
        className={group({ size, isCard, isError, hideIndicator, className })}
        {...props}
      />
    </RadioGroupContext.Provider>
  );
};

type IndicatorProps = ComponentProps<typeof RadioGroupPrimitive.Item> &
  Pick<RadioGroupVariants, "isError">;

const Indicator = ({
  className,
  isError: isErrorProp,
  "aria-invalid": ariaInvalid,
  ...props
}: IndicatorProps) => {
  const { size, hideIndicator } = useRadioGroupContext();

  const isError = typeof ariaInvalid === "boolean" ? ariaInvalid : isErrorProp;

  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-indicator"
      className={input({ size, isError, hideIndicator, className })}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        className={indicator({ size, isError, hideIndicator, className })}
      />
    </RadioGroupPrimitive.Item>
  );
};

const Item = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>) => {
  const { size, isCard, isError } = useRadioGroupContext();

  return (
    <label
      data-slot="radio-group-item"
      className={itemRoot({ size, isCard, isError, className })}
    >
      <Indicator isError={isError} {...props} />

      <div className="grow">{children}</div>
    </label>
  );
};

const Label = ({ className, ...props }: ComponentProps<"span">) => {
  return (
    <span
      data-slot="radio-group-label"
      className={label({ className })}
      {...props}
    />
  );
};

const Description = ({ className, ...props }: ComponentProps<"span">) => {
  return (
    <span
      data-slot="radio-group-description"
      className={description({ className })}
      {...props}
    />
  );
};

export const RadioGroup = {
  Root,
  Indicator,
  Item,
  Label,
  Description,
};
